import { createServerFn } from "@tanstack/react-start";

import { requireAdminOrRop } from "@/auth/middleware";
import { sanitizeForSeroval } from "@/server/seroval-safe";
import { readLearningDiffs } from "./learning-store";
import { readLibrary, writeLibrary } from "./library-store";
import type { BlocksLibrary } from "./mocks";
import { readRatecard, writeRatecard } from "./ratecard-store";
import {
  applySuggestion,
  decide,
  decidedKeys,
  isAuto,
  modeOf,
  suggestFromCorpus,
  suggestFromDiffs,
  suggestionTitle,
  type LearningLogEntry,
  type LearningMode,
  type Suggestion,
  type SuggestionKind,
  revertFor,
} from "./library-suggestions";

/**
 * Чему научился справочник — и что из этого он применил сам.
 *
 * Считается заново при каждом открытии: предложения зависят и от выпущенных
 * смет, и от самого справочника, а устаревший список здесь опаснее лишнего
 * запроса — принять предложение, которое уже применено, значит поверить, что
 * система забыла собственное решение.
 *
 * Здесь же происходит автоприменение. Место выбрано осознанно: фонового
 * планировщика в продукте нет, и заводить его ради двух безопасных видов
 * предложений — несоразмерно. «Само» означает, что руководителю не надо
 * принимать решение, а не что это случится ночью: применится при первом же
 * заходе любого, кто отвечает за справочник. Запись на чтении здесь не
 * новость — так же ведёт себя пополнение справочника из зерна.
 *
 * Только admin и РОП: правки одного менеджера — это его привычки, а правило
 * агентства выводится из общей выборки.
 */

export interface LearningState {
  suggestions: Suggestion[];
  log: LearningLogEntry[];
  policy: Record<SuggestionKind, LearningMode>;
  /** что система применила сама прямо сейчас — чтобы экран это назвал */
  appliedNow: string[];
  /**
   * Справочник после автоприменения.
   *
   * Стор читает справочник один раз за сессию, а здесь он только что
   * поменялся — на сервере. Без этого поля следующая правка с экрана ушла бы
   * со старой отметкой и получила конфликт на ровном месте, а выученное
   * пропадало бы: ровно это и происходило, пока запись шла без условия.
   */
  library?: BlocksLibrary;
}

const _loadLearning = createServerFn({ method: "GET" })
  .middleware([requireAdminOrRop])
  .handler(async (): Promise<LearningState> => {
    const [diffs, libraryRaw, ratecardRaw] = await Promise.all([
      readLearningDiffs(),
      readLibrary(),
      readRatecard(),
    ]);

    let library = libraryRaw;
    let ratecard = ratecardRaw;
    const appliedNow: string[] = [];

    // Применяем по одному, пересчитывая список: принятое предложение меняет
    // справочник, а значит и то, что имеет смысл предлагать дальше.
    for (let step = 0; step < 20; step++) {
      const all = suggestFromDiffs(diffs, library, ratecard, decidedKeys(library));
      const next = all.find((s) => isAuto(s, library.learningPolicy));
      if (!next) break;

      const applied = applySuggestion(next, library, ratecard);
      const title = suggestionTitle(next);
      library = decide(applied.library, next, "auto", {
        title,
        revert: revertFor(next, { library }, applied),
      });
      ratecard = applied.ratecard;
      appliedNow.push(title);
    }

    let saved: BlocksLibrary | undefined;
    if (appliedNow.length > 0) {
      const write = await writeLibrary(library);
      if (write.ok) {
        saved = write.library;
        library = write.library;
        if (ratecard !== ratecardRaw) await writeRatecard(ratecard);
        console.info(`[learning] применено само: ${appliedNow.length}`);
      } else {
        // Справочник успели поменять, пока мы считали: применённое не
        // записалось, и говорить, что оно применено, нельзя. Берём свежий
        // документ и показываем предложения по нему — следующий заход
        // применит их заново.
        console.warn("[learning] автоприменение разошлось с базой — считаем по свежему");
        library = write.current;
        ratecard = ratecardRaw;
        saved = write.current;
        appliedNow.length = 0;
      }
    }

    const decided = decidedKeys(library);
    // Два источника, один список: правки в живых сметах и обход папки с
    // калькуляторами. Архивные идут следом за выученными у менеджеров —
    // свежая правка сегодняшней сметы говорит о работе агентства больше, чем
    // строка из книги трёхлетней давности.
    const suggestions = [
      ...suggestFromDiffs(diffs, library, ratecard, decided),
      ...suggestFromCorpus(library, ratecard, decided),
    ];
    const policy = Object.fromEntries(
      (Object.keys(POLICY_ORDER) as SuggestionKind[]).map((k) => [
        k,
        modeOf(k, library.learningPolicy),
      ]),
    ) as Record<SuggestionKind, LearningMode>;

    return sanitizeForSeroval("loadLearning", {
      suggestions,
      log: library.learningLog ?? [],
      policy,
      appliedNow,
      library: saved,
    });
  });

export const loadLearning = _loadLearning;

/** Порядок видов на экране: от безопасного к денежному. */
export const POLICY_ORDER: Record<SuggestionKind, string> = {
  ratecard_role: "Новая роль в прайсе",
  new_block: "Новый блок из смет",
  corpus_block: "Новый блок из папки с калькуляторами",
  block_line_added: "Новая строка в эталоне блока",
  block_line_removed: "Строка уходит из эталона блока",
  block_hours: "Часы в эталоне блока",
  ratecard_rate: "Ставка в прайсе",
};
